import type { DataAccessType } from '@/features/DataAccessStatus';
import type { ColumnsConfig, ColumnsMeta } from '@/entities/columnsConfig';
import { baseApi, getErrorMessage } from '@/shared/api';
import { convertUTCtoISO } from '@/shared/lib';
import type { DataUpdateInfo } from '../model/types';
import type {
  ColumnsMetaDTO,
  DataResponse,
  DataUpdateInfoDTO,
  GetDataParams,
  GetMetricsMinMaxParams,
  GetNumberFilterParams,
  GetStringFilterParams,
  GetTotalParams,
  GetTreeDataParams,
  TotalResponse,
  TreeDataResponse,
} from './types';

export const getDataUpdateInfo = async (): Promise<DataUpdateInfo> => {
  const { data, error } = await baseApi.functions.invoke<DataUpdateInfoDTO>(
    'getCalendarInsightsDataUpdateInfo',
  );

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return {
    updateTime: convertUTCtoISO(data.updateTime),
  };
};

export const getData = async ({
  dimensions,
  metrics,
  cohortDateScale,
  cohortDateRange,
  revenueDateScale,
  revenueDateRange,
  offset,
  sort,
  probabilisticAttribution,
  dimensionsFilters,
  metricsFilters,
  pageSize,
}: GetDataParams) => {
  const { data, error } = await baseApi.functions.invoke<DataResponse>(
    'getCalendarInsightsRows',
    {
      body: {
        dimensions,
        metrics,
        cohortDateScale,
        cohortDateRange,
        revenueDateScale,
        revenueDateRange,
        dimensionsFilters,
        metricsFilters,
        probabilisticAttribution,
        sort,
        offset,
        pageSize,
      },
    },
  );

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return data;
};

export const getTotal = async ({
  dimensions,
  metrics,
  dimensionsFilters,
  cohortDateRange,
  revenueDateRange,
  probabilisticAttribution,
}: GetTotalParams) => {
  const { data, error } = await baseApi.functions.invoke<TotalResponse>(
    'getCalendarInsightsTotal',
    {
      body: {
        metrics,
        dimensions,
        cohortDateRange,
        revenueDateRange,
        probabilisticAttribution,
        dimensionsFilters,
      },
    },
  );

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return data.total;
};

export const getTreeData = async ({
  dimensions,
  metrics,
  cohortDateRange,
  cohortDateScale,
  revenueDateRange,
  revenueDateScale,
  sort,
  probabilisticAttribution,
  dimensionsFilters,
  metricsFilters,
  parentGroups,
}: GetTreeDataParams) => {
  const { data, error } = await baseApi.functions.invoke<TreeDataResponse>(
    'getCalendarInsightsTreeViewData',
    {
      body: {
        dimensions,
        metrics,
        cohortDateRange,
        cohortDateScale,
        revenueDateRange,
        revenueDateScale,
        sort,
        probabilisticAttribution,
        dimensionsFilters,
        metricsFilters,
        parentGroups,
      },
    },
  );

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return data;
};

export const getColumnsMeta = async (): Promise<ColumnsMeta> => {
  const { data, error } = await baseApi.functions.invoke<ColumnsMetaDTO>(
    'getCalendarInsightsColumns',
  );

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return {
    defaultColumns: {
      dimensions: data.defaultColumns.dimensions,
      metrics: data.defaultColumns.metrics,
    },
    config: Object.fromEntries(
      Object.entries(data.config).map(([key, value]) => {
        return [
          key,
          {
            group: value.ui_column_group,
            name: value.ui_column_name,
            description: value.ui_column_description,
            type: value.ui_column_format,
            order: value.ui_calendar_view_drawer_filter_order,
            dataType: value.ui_column_data_type,
            isAdvancedSortable: value.ui_is_advanced_sorting_available,
          },
        ];
      }),
    ) as ColumnsConfig,
    dimensions: data.dimensions,
    metrics: data.metrics,
  };
};

export const getNumberFilterParams = async (params: GetNumberFilterParams) => {
  const { data, error } = await baseApi.functions.invoke<{
    min: number;
    max: number;
  }>('getCalendarInsightsNumberFilterParams', {
    body: {
      filterName: params.filterName,
      dimensions: params.dimensions,
      dimensionsFilters: params.dimensionsFilters,
      metricsFilters: params.metricsFilters,
      cohortDateRange: params.cohortDateRange,
      cohortDateScale: params.cohortDateScale,
      revenueDateRange: params.revenueDateRange,
      revenueDateScale: params.revenueDateScale,
      probabilisticAttribution: params.probabilisticAttribution,
      groupedData: params.groupedData,
    },
  });

  if (!data) {
    throw new Error(getErrorMessage(error));
  }

  return data;
};

export const getStringFilterParams = async (params: GetStringFilterParams) => {
  const filtersWithoutCurrent = params.dimensionsFilters.filter((filter) => {
    return filter.id !== params.filterName;
  });

  const { data, error } = await baseApi.functions.invoke<{
    options: string[];
  }>('getCalendarInsightsStringFilterParams', {
    body: {
      filterName: params.filterName,
      dimensionsFilters: filtersWithoutCurrent,
      cohortDateRange: params.cohortDateRange,
      revenueDateRange: params.revenueDateRange,
    },
  });

  if (!data) {
    throw new Error(getErrorMessage(error));
  }

  return data;
};

export const getMetricsMinMax = async (params: GetMetricsMinMaxParams) => {
  const { data, error } = await baseApi.functions.invoke<
    Record<
      string,
      {
        min: number;
        max: number;
      }
    >
  >('getCalendarInsightsMetricsMinMax', {
    body: {
      metrics: params.metricNames,
      dimensions: params.selectedDimensions,
      dimensionsFilters: params.dimensionsFilters,
      metricsFilters: params.metricsFilters,
      cohortDateRange: params.cohortDateRange,
      cohortDateScale: params.cohortDateScale,
      revenueDateRange: params.revenueDateRange,
      revenueDateScale: params.revenueDateScale,
      probabilisticAttribution: params.probabilisticAttribution,
      groupedData: params.groupedData,
    },
  });

  if (!data) {
    throw new Error(getErrorMessage(error));
  }

  return data;
};

interface GetDataAccessParams {
  metrics: string[];
}

export const getDataAccess = async (
  params: GetDataAccessParams,
): Promise<DataAccessType> => {
  const { data, error } = await baseApi.functions.invoke<{
    accessType: DataAccessType;
  }>('getCalendarInsightsDataAccessInfo', {
    body: {
      presetDetails: {
        metrics: params.metrics,
      },
    },
  });

  if (!data) {
    throw new Error(getErrorMessage(error));
  }

  return data.accessType;
};
